// The progression ladder: a fixed list of levels, plus what the player has
// done with them.
//
// A level is nothing but a recipe — mode, size, difficulty and a seed. The
// generators are deterministic once seeded (see rng.ts), so the recipe is
// enough to rebuild the exact same board anywhere, any number of times. That
// is what lets level 17 be the same puzzle on every device, and what lets a
// finished level be replayed rather than merely re-rolled.

import type { BoardMode } from './crossword.js';

export interface Level {
  n: number;          // 1-based level number, as shown on the cell
  mode: BoardMode;
  sizeIndex: number;  // 0..3 — S / M / L / XL, as in free play
  diffIndex: number;  // 0..2 — Easy / Normal / Hard
  seed: number;
}

export const LEVEL_COLS  = 6;
export const LEVEL_ROWS  = 10;
export const LEVEL_COUNT = LEVEL_COLS * LEVEL_ROWS;

/** How many unfinished levels may stand open at once. */
export const MAX_OPEN = 5;

// Last level of each size band. Bands get longer as they go, so the biggest
// boards — which are also the slowest to solve — aren't rushed past.
const SIZE_BANDS = [6, 18, 36, LEVEL_COUNT];

// A full grid is a two-dimensional gradient to be read with only two corners
// given, which is far more to hold in your head at once than either of the
// other two. It stays out of the ladder until here.
const GRID_FROM = 30;

// Which type each level is, as a repeating pattern either side of that.
const MODE_EARLY: BoardMode[] = ['edges', 'edges', 'cross', 'edges', 'cross', 'cross'];
const MODE_LATE:  BoardMode[] = ['grid', 'cross', 'edges', 'grid', 'cross', 'edges', 'grid', 'cross'];

// Full grids don't inherit the size and difficulty the rest of the ladder has
// reached by level 30 — they climb a ladder of their own, stepped by how many
// have been played rather than by how far down the ladder they sit, so the
// first one is a gentle introduction wherever it lands. As everywhere else, a
// bigger board may start over at Easy, but it never eases off at one size.
const GRID_STEPS: [number, number][] = [
  [1, 0], [1, 1], [2, 1], [2, 2], [3, 0], [3, 1], [3, 1], [3, 2],
];

/** A stable, well-spread seed for a level number. */
function seedFor(n: number): number {
  let h = Math.imul(n, 0x9E3779B1) >>> 0;
  h = Math.imul(h ^ (h >>> 16), 0x85EBCA6B) >>> 0;
  h = Math.imul(h ^ (h >>> 13), 0xC2B2AE35) >>> 0;
  return (h ^ (h >>> 16)) >>> 0;
}

function buildLadder(): Level[] {
  const ladder: Level[] = [];
  let grids = 0; // full grids so far, which is what sizes the next one

  for (let n = 1; n <= LEVEL_COUNT; n++) {
    const mode = n < GRID_FROM
      ? MODE_EARLY[(n - 1) % MODE_EARLY.length]
      : MODE_LATE[(n - GRID_FROM) % MODE_LATE.length];

    const band  = SIZE_BANDS.findIndex(last => n <= last);
    const first = band === 0 ? 1 : SIZE_BANDS[band - 1] + 1;
    const span  = SIZE_BANDS[band] - first + 1;
    // Each band walks Easy → Normal → Hard before the next size takes over. The
    // opening band stops at Normal — Hard hues on level five is no way to teach
    // anyone what the game is asking for.
    const maxDiff = band === 0 ? 1 : 2;

    let sizeIndex = band;
    let diffIndex = Math.min(maxDiff, Math.floor(((n - first) / span) * 3));

    if (mode === 'grid') {
      [sizeIndex, diffIndex] = GRID_STEPS[Math.min(grids, GRID_STEPS.length - 1)];
      grids++;
    }

    ladder.push({ n, mode, sizeIndex, diffIndex, seed: seedFor(n) });
  }

  return ladder;
}

export const LEVELS: Level[] = buildLadder();

/**
 * How far down the ladder is open, given how many levels are done.
 *
 * Finishing one level opens two more, but never so many that more than
 * MAX_OPEN unfinished levels are sitting open at the same time.
 */
export function unlockedCount(completed: number): number {
  return Math.min(LEVEL_COUNT, 1 + completed * 2, completed + MAX_OPEN);
}

// ── Saved progress ───────────────────────────────────────────────────────────

const STORAGE_KEY = 'chroma.progress.v1';

export interface Progress {
  /** Level number → fewest moves it has been solved in. */
  best: Record<number, number>;
  /** How much of the ladder the player has already seen open, so newly
   *  unlocked levels can be announced exactly once. */
  seen: number;
}

function emptyProgress(): Progress {
  return { best: {}, seen: 0 };
}

export function loadProgress(): Progress {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return emptyProgress();
    const parsed = JSON.parse(raw) as Partial<Progress>;
    const best: Record<number, number> = {};
    for (const [k, v] of Object.entries(parsed.best ?? {})) {
      const n = Number(k);
      if (Number.isInteger(n) && n >= 1 && n <= LEVEL_COUNT && typeof v === 'number') {
        best[n] = v;
      }
    }
    return { best, seen: typeof parsed.seen === 'number' ? parsed.seen : 0 };
  } catch {
    // Private browsing, a full quota, hand-edited junk — play on regardless.
    return emptyProgress();
  }
}

export function saveProgress(progress: Progress): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(progress));
  } catch {
    // Nothing to be done about it, and it's not worth interrupting a game for.
  }
}

export function completedCount(progress: Progress): number {
  return Object.keys(progress.best).length;
}

export function isCompleted(progress: Progress, n: number): boolean {
  return progress.best[n] !== undefined;
}

export function isUnlocked(progress: Progress, n: number): boolean {
  return n <= unlockedCount(completedCount(progress));
}

/** Record a solve, keeping the best move count. Returns the updated progress. */
export function recordSolve(progress: Progress, n: number, moves: number): Progress {
  const prev = progress.best[n];
  if (prev === undefined || moves < prev) progress.best[n] = moves;
  saveProgress(progress);
  return progress;
}
